import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../../firebase-client';

const text = (value) => String(value ?? '').trim();
const timestampMillis = (value) => value?.toMillis?.() || 0;

function memberName(member = {}) {
  const fullName = [text(member.firstName), text(member.lastName)].filter(Boolean).join(' ');
  return text(member.displayName || fullName || member.username || member.name || member.email) || 'Member';
}

function memberTags(member = {}) {
  if (Array.isArray(member.tags)) return member.tags.map((tag) => text(typeof tag === 'string' ? tag : tag?.name)).filter(Boolean);
  return Object.entries(member.tags || {}).filter(([, active]) => active === true).map(([name]) => name);
}

function canCheck(member, settings, canManage) {
  if (canManage) return true;
  const allowed = new Set(settings.checkerTagNames || []);
  return allowed.size > 0 && memberTags(member).some((tag) => allowed.has(tag));
}

function statusFor(acceptance, versionId) {
  if (!acceptance?.versionId) return 'missing';
  return acceptance.versionId === versionId ? 'current' : 'outdated';
}

function profileFields(profileSnap) {
  const config = profileSnap.exists() ? profileSnap.data() || {} : {};
  const configured = Array.isArray(config.fields) ? config.fields : [];
  return { config, configured, active: config.enabled === false ? [] : configured };
}

function looksLikeIamField(field = {}) {
  const label = text(field.label).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return label.includes('iam') && label.includes('membership') && (label.includes('number') || label.includes('no'));
}

function signatureStrokes(value) {
  if (!Array.isArray(value)) return [];
  return value.map((stroke) => {
    const points = Array.isArray(stroke?.points) ? stroke.points : (Array.isArray(stroke) ? stroke : []);
    return points.map((point) => Array.isArray(point) ? [Number(point[0]), Number(point[1])] : [Number(point?.x), Number(point?.y)])
      .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  }).filter((stroke) => stroke.length > 0);
}

export async function loadRideDeclarationStatus({ teamId, team, userId, member, canManage }) {
  const latestVersionQuery = query(
    collection(db, 'teams', teamId, 'rideDeclaration', 'main', 'versions'),
    orderBy('versionNumber', 'desc'),
    limit(1),
  );
  const [teamSnap, versionsSnap, acceptanceSnap, profileSnap, memberSnap] = await Promise.all([
    getDoc(doc(db, 'teams', teamId)),
    getDocs(latestVersionQuery),
    getDoc(doc(db, 'teams', teamId, 'rideDeclarationAcceptances', userId)),
    getDoc(doc(db, 'teams', teamId, 'profileFields', 'config')),
    member ? Promise.resolve(null) : getDoc(doc(db, 'teams', teamId, 'members', userId)),
  ]);
  const currentTeam = teamSnap.exists() ? teamSnap.data() || {} : team || {};
  const settings = currentTeam?.modules?.iamRoadSmart?.rideDeclarations || {};
  const resolvedMember = member || (memberSnap?.exists() ? { id: memberSnap.id, ...memberSnap.data() } : {});
  const versionDoc = versionsSnap.docs[0] || null;
  const version = versionDoc ? { versionId: versionDoc.id, ...versionDoc.data() } : null;
  const acceptance = acceptanceSnap.exists() ? acceptanceSnap.data() || {} : null;
  const fields = profileFields(profileSnap).active;
  const membershipField = fields.find((field) => text(field.id) === text(settings.membershipNumberProfileFieldId)) || null;
  const membershipNumber = membershipField ? text(resolvedMember?.profileFields?.[membershipField.id]) : '';
  return {
    canManage,
    canCheck: canCheck(resolvedMember, settings, canManage),
    memberName: memberName(resolvedMember),
    membershipNumber,
    membershipFieldConfigured: Boolean(membershipField),
    status: version ? statusFor(acceptance, version.versionId) : 'unavailable',
    version,
    acceptedAt: acceptance?.acceptedAt || null,
  };
}

export async function loadRideDeclarationAdminOverview({ teamId, team, members }) {
  const versionsQuery = query(
    collection(db, 'teams', teamId, 'rideDeclaration', 'main', 'versions'),
    orderBy('versionNumber', 'desc'),
  );
  const [teamSnap, rootSnap, versionsSnap, acceptancesSnap, checksSnap, profileSnap] = await Promise.all([
    getDoc(doc(db, 'teams', teamId)),
    getDoc(doc(db, 'teams', teamId, 'rideDeclaration', 'main')),
    getDocs(versionsQuery),
    getDocs(collection(db, 'teams', teamId, 'rideDeclarationAcceptances')),
    getDocs(collection(db, 'teams', teamId, 'rideDeclarationChecks')),
    getDoc(doc(db, 'teams', teamId, 'profileFields', 'config')),
  ]);
  const currentTeam = teamSnap.exists() ? teamSnap.data() || {} : team || {};
  const root = rootSnap.exists() ? rootSnap.data() || {} : {};
  const settings = currentTeam?.modules?.iamRoadSmart?.rideDeclarations || {};
  const acceptanceMap = new Map(acceptancesSnap.docs.map((snapshot) => [snapshot.id, snapshot.data() || {}]));
  const latestCheckMap = new Map();
  checksSnap.docs.forEach((snapshot) => {
    const row = snapshot.data() || {};
    if (!latestCheckMap.has(row.memberId) || timestampMillis(row.checkedAt) > timestampMillis(latestCheckMap.get(row.memberId))) latestCheckMap.set(row.memberId, row.checkedAt || null);
  });
  const fields = profileFields(profileSnap);
  const membershipField = fields.active.find((field) => text(field.id) === text(settings.membershipNumberProfileFieldId)) || null;
  const memberRows = (members || []).map((member) => {
    const acceptance = acceptanceMap.get(member.id) || null;
    const membershipNumber = membershipField ? text(member?.profileFields?.[membershipField.id]) : '';
    const declarationStatus = root.latestVersionId ? statusFor(acceptance, root.latestVersionId) : 'unavailable';
    return {
      _memberId: member.id,
      name: memberName(member),
      membershipNumber,
      status: membershipField && !membershipNumber ? 'missing_membership_number' : declarationStatus,
      acceptedAt: acceptance?.acceptedAt || null,
      latestCheckAt: latestCheckMap.get(member.id) || null,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
  return {
    settings,
    draftWording: root.draftWording || '',
    draftNeedsPublishing: root.draftNeedsPublishing === true,
    latestVersionId: root.latestVersionId || '',
    latestVersionNumber: Number(root.latestVersionNumber || 0),
    profileSetup: {
      moduleEnabled: currentTeam?.modules?.profile?.enabled === true,
      fieldsEnabled: fields.config.enabled !== false && fields.configured.length > 0,
      recommendedFieldId: text(fields.configured.find(looksLikeIamField)?.id),
      fields: fields.configured.map((field) => ({ id: text(field.id), label: text(field.label), type: text(field.type) || 'text' })).filter((field) => field.id && field.label),
    },
    versions: versionsSnap.docs.map((snapshot) => ({ versionId: snapshot.id, ...snapshot.data() })),
    members: memberRows,
  };
}

export async function loadRideDeclarationMemberHistory({ teamId, memberId, member, includeChecksRecorded = false }) {
  const [acceptancesSnap, checksSnap, checksRecordedSnap] = await Promise.all([
    getDocs(collection(db, 'teams', teamId, 'rideDeclarationAcceptances', memberId, 'versions')),
    getDocs(query(collection(db, 'teams', teamId, 'rideDeclarationChecks'), where('memberId', '==', memberId))),
    includeChecksRecorded
      ? getDocs(query(collection(db, 'teams', teamId, 'rideDeclarationChecks'), where('checkerId', '==', memberId)))
      : Promise.resolve(null),
  ]);
  const acceptances = acceptancesSnap.docs.map((snapshot) => {
    const row = snapshot.data() || {};
    return {
      versionId: row.versionId || snapshot.id,
      versionNumber: Number(row.versionNumber || 0),
      wording: row.declarationWording || '',
      membershipNumber: row.membershipNumber || '',
      signatureStrokes: signatureStrokes(row.signatureStrokes),
      signatureHash: row.signatureHash || '',
      acceptedAt: row.acceptedAt || null,
    };
  }).sort((a, b) => b.versionNumber - a.versionNumber);
  const checks = checksSnap.docs.map((snapshot) => {
    const row = snapshot.data() || {};
    return { checkerName: row.checkerName || 'Checker', versionNumber: Number(row.versionNumber || 0), checkedAt: row.checkedAt || null, syncedAt: row.syncedAt || null, offline: row.offline === true };
  }).sort((a, b) => timestampMillis(b.checkedAt) - timestampMillis(a.checkedAt));
  const recordedRows = (checksRecordedSnap?.docs || []).map((snapshot) => snapshot.data() || {});
  const fallbackMemberIds = Array.from(new Set(recordedRows
    .filter((row) => !text(row.memberName) || text(row.memberName) === 'Member')
    .map((row) => text(row.memberId)).filter(Boolean)));
  const fallbackProfiles = await Promise.all(fallbackMemberIds.map((id) => getDoc(doc(db, 'users', id))));
  const fallbackNames = new Map(fallbackProfiles.filter((snapshot) => snapshot.exists()).map((snapshot) => [snapshot.id, memberName(snapshot.data() || {})]));
  const checksRecorded = recordedRows.map((row) => {
    const savedName = text(row.memberName);
    return { memberName: savedName && savedName !== 'Member' ? savedName : fallbackNames.get(text(row.memberId)) || 'Member', versionNumber: Number(row.versionNumber || 0), checkedAt: row.checkedAt || null, syncedAt: row.syncedAt || null, offline: row.offline === true };
  }).sort((a, b) => timestampMillis(b.checkedAt) - timestampMillis(a.checkedAt));
  return { memberName: memberName(member), acceptances, checks, checksRecorded };
}
